import { memo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { IconButton } from '@cyphex/ui';

export interface GridPagerProps {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

/**
 * Pagination is a bandwidth control, not a nicety: publishers on other pages
 * are unsubscribed, so a 200-person room still costs one page of video.
 */
export const GridPager = memo(function GridPager({
  page, pageCount, pageSize, total, onPageChange,
}: GridPagerProps) {
  if (pageCount <= 1) return null;
  const first = page * pageSize + 1;
  const last = Math.min(total, (page + 1) * pageSize);

  return (
    <nav aria-label="Video pages" className="flex shrink-0 items-center justify-center gap-2 pb-1">
      <IconButton
        icon={ChevronLeft}
        label="Previous page"
        size="sm"
        variant="outline"
        disabled={page === 0}
        onClick={() => onPageChange(page - 1)}
      />
      <span className="text-sm tabular text-muted-foreground">
        {first}–{last} of {total}
      </span>
      <IconButton
        icon={ChevronRight}
        label="Next page"
        size="sm"
        variant="outline"
        disabled={page >= pageCount - 1}
        onClick={() => onPageChange(page + 1)}
      />
    </nav>
  );
});
